/* eslint-disable react/prop-types */
import React, { useState } from "/vendor/react";
import PropTypes from "prop-types";
import gql from "/vendor/graphql-tag";
import {
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "/vendor/@material-ui/core";
import { TableListEmptyState, Loader } from "/lib/component/base";
import { ListController } from "/lib/component/controller";
import Pagination from "/lib/component/partial/Pagination";
import SilenceEntryDialog from "/lib/component/partial/SilenceEntryDialog";
import ClearSilencedEntriesDialog from "/lib/component/partial/ClearSilencedEntriesDialog";
import EntitiesListHeader from "./EntitiesListHeader";
import EntitiesListItem from "./EntitiesListItem";

function EntitiesList(props) {
  const [silenceState, setSilenceState] = useState(null);
  const [unsilenceState, setUnsilenceState] = useState(null);
  const [customFilter, setCustomFilter] = React.useState(null);

  const deleteEntities = entities => {
    entities.forEach(({ id }) => props.onDelete({ id }));
  };

  const silenceItems = entities => {
    const targets = entities
      .filter(entity => entity.silences.length === 0)
      .map(entity => ({
        namespace: entity.namespace,
        check: "*",
        subscription: `entity:${entity.name}`,
      }));

    if (targets.length === 1) {
      setSilenceState({
        props: {},
        ...targets[0],
      });
    } else if (targets.length) {
      setSilenceState({
        props: {},
        targets,
      });
    }
  };

  const clearSilences = items => {
    setUnsilenceState(
      items.reduce((memo, item) => [...memo, ...item.silences], []),
    );
  };

  const renderEmptyState = () => {
    return (
      <TableRow>
        <TableCell>
          <TableListEmptyState
            loading={props.loading}
            primary="No results matched your query."
            secondary="Try refining your search query in the search box. The filter buttons above are also a helpful way of quickly finding entities."
          />
        </TableCell>
      </TableRow>
    );
  };

  const renderEntity = ({
    key,
    item: entity,
    hovered,
    setHovered,
    selectedCount,
    selected,
    setSelected,
  }) => (
    <EntitiesListItem
      key={key}
      editable={props.editable}
      editing={selectedCount > 0}
      entity={entity}
      hovered={hovered}
      onHover={props.editable ? setHovered : () => null}
      selected={selected}
      onChangeSelected={setSelected}
      onClickDelete={() => deleteEntities([entity])}
      onClickSilence={() => silenceItems([entity])}
      onClickClearSilence={() => clearSilences([entity])}
    />
  );

  const items = props.namespace
    ? props.namespace.entities.nodes.filter(entity => !entity.deleted)
    : [];

  function handleCustomFilterSort() {
    if (customFilter === "WINDOWID") {
      return [...items].sort((a, b) => {
        const event = a?.events?.find(
          event => event?.check?.name === "window-id-log",
        );
        const match = event?.check?.output?.match(/:\s+(\d+)/);
        const windowIDA = match && match[1];

        const eventb = b?.events?.find(
          event => event?.check?.name === "window-id-log",
        );
        const matchb = eventb?.check?.output?.match(/:\s+(\d+)/);
        const windowIDB = matchb && matchb[1];

        // When windowID is undefined for both items, keep their original order
        if (!windowIDA && !windowIDB) {
          return 0;
        }
        // When windowID is undefined for the first item, it goes after the second item
        if (!windowIDA) {
          return 1;
        }
        // When windowID is undefined for the second item, it goes after the first item
        if (!windowIDB) {
          return -1;
        }
        if (windowIDA < windowIDB) {
          return -1;
        }
        if (windowIDA > windowIDB) {
          return 1;
        }
        return 0;
      });
    }
    if (customFilter === "LOCATION") {
      return [...items].sort((a, b) => {
        const locationEventA = a?.events?.find(
          event => event?.check?.name === "location",
        );
        const locationMatchA = locationEventA?.check?.output?.match(
          /City: (.*?), Region: (.*?),/,
        );
        const locationA =
          locationMatchA && `${locationMatchA[1]}, ${locationMatchA[2]}`;

        const locationEventB = b?.events?.find(
          event => event?.check?.name === "location",
        );
        const locationMatchB = locationEventB?.check?.output?.match(
          /City: (.*?), Region: (.*?),/,
        );
        const locationB =
          locationMatchB && `${locationMatchB[1]}, ${locationMatchB[2]}`;
        // replace 'location' with the path to the location property in your objects

        return locationA?.localeCompare(locationB);
      });
    }
    return items;
  }

  const sortedItems = !customFilter
    ? items
    : handleCustomFilterSort(items, customFilter);

  return (
    <ListController
      items={sortedItems}
      getItemKey={entity => entity.id}
      renderEmptyState={renderEmptyState}
      renderItem={renderEntity}
    >
      {({ children, selectedItems, setSelectedItems, toggleSelectedItems }) => (
        <Paper>
          <Loader loading={props.loading}>
            <EntitiesListHeader
              editable={props.editable}
              filters={props.filters}
              selectedItems={selectedItems}
              rowCount={children.length || 0}
              onClickSelect={toggleSelectedItems}
              onClickDelete={() => deleteEntities(selectedItems)}
              onClickSilence={() => silenceItems(selectedItems)}
              onClickClearSilences={() => clearSilences(selectedItems)}
              onChangeFilters={props.onChangeFilters}
              onChangeQuery={props.onChangeQuery}
              onChangeCustomFilter={setCustomFilter}
              redirectEntity={props.redirectEntity}
              namespace={props.namespace}
              order={props.order}
            />

            <Table>
              <TableBody>{children}</TableBody>
            </Table>

            <Pagination
              limit={props.limit}
              offset={props.offset}
              pageInfo={props.namespace && props.namespace.entities.pageInfo}
              onChangeQuery={update =>
                props.onChangeQuery(params => ({ ...params, ...update }))
              }
            />

            <ClearSilencedEntriesDialog
              silences={unsilenceState}
              open={!!unsilenceState}
              onSave={props.onDeleteSilence}
              onClose={() => {
                setUnsilenceState(null);
                setSelectedItems([]);
                props.refetch && props.refetch();
              }}
            />

            {silenceState && (
              <SilenceEntryDialog
                values={silenceState}
                onSave={props.onCreateSilence}
                onClose={() => {
                  setSilenceState(null);
                  setSelectedItems([]);
                  props.refetch && props.refetch();
                }}
              />
            )}
          </Loader>
        </Paper>
      )}
    </ListController>
  );
}

EntitiesList.propTypes = {
  editable: PropTypes.bool,
  loading: PropTypes.bool,
  namespace: PropTypes.object,
  order: PropTypes.string.isRequired,
  filters: PropTypes.array.isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  onChangeQuery: PropTypes.func.isRequired,
  onCreateSilence: PropTypes.func.isRequired,
  onDeleteSilence: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  offset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  refetch: PropTypes.func,
  redirectEntity: PropTypes.func.isRequired,
};

EntitiesList.defaultProps = {
  editable: false,
  loading: false,
  limit: undefined,
  namespace: null,
  offset: undefined,
  refetch: () => null,
};

EntitiesList.fragments = {
  namespace: gql`
    fragment EntitiesList_namespace on Namespace {
      entities(
        limit: $limit
        offset: $offset
        filters: $filters
        orderBy: $order
      ) @connection(key: "entities", filter: ["filters", "orderBy"]) {
        nodes {
          id
          namespace
          deleted @client

          silences {
            ...ClearSilencedEntriesDialog_silence
          }

          ...EntitiesListItem_entity
        }

        pageInfo {
          ...Pagination_pageInfo
        }
      }
      ...EntitiesListHeader_namespace
    }

    ${ClearSilencedEntriesDialog.fragments.silence}
    ${EntitiesListHeader.fragments.namespace}
    ${EntitiesListItem.fragments.entity}
    ${Pagination.fragments.pageInfo}
  `,
};

export default EntitiesList;
